import type { LocaleType } from './Locale';

export interface IResource {
  is?: string | any;
  en?: string | any;
  pl?: string | any;
}

/**
 * Returns a count specific resource object for locale or fallback to is, en or empty
 * For count specific resource properties for none, singular or plural must be provided
 **/
const getCountSpecificResourceKey = (
  locale: LocaleType,
  resources: IResource,
) => {
  const resourceObject = resources[locale];
  const undefinedCheck = (object: any) => {
    return (
      object !== undefined &&
      object.zero !== undefined &&
      object.one !== undefined &&
      object.many !== undefined
    );
  };

  if (undefinedCheck(resourceObject)) {
    return resourceObject;
  }
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      `Missing resource count specific key '${resourceObject.toString()}' for locale '${locale}'`,
    );
  }
  if (undefinedCheck(resources.is)) {
    return resources.is;
  }
  if (undefinedCheck(resources.en)) {
    return resources.en;
  }

  return { zero: '', one: '', many: '' };
};

const getResources = (locale: LocaleType, resources: IResource) => {
  const resource = resources[locale];
  if (resource !== undefined) {
    return resource;
  }
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      `Missing resource key '${resources.toString()}' for locale '${locale}'`,
    );
  }
  if (resources.is !== undefined) {
    return resources.is;
  }
  if (resources.en !== undefined) {
    return resources.en;
  }
  return '';
};

/**
 * Get a resources string depending on locale and the count.
 * @param {Locale} locale - A string representing the locale.
 * @param {object} resources - A resource object. The key in the resource object must contain an object with keys none, singular, plural
 * @param {int} count - The count that defines if none, singular or plural resource should be returned. Undefined if not count specific.
 * @return {string} String depending on the count.
 */
const getCountSpecificResource = (
  locale: LocaleType,
  resources: IResource,
  count: number,
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  // Count must be a whole number
  if (isNaN(count) || count % 1 !== 0) {
    return '';
  }
  const resourceObject = getCountSpecificResourceKey(locale, resources);

  if (count === 0) {
    return resourceObject.zero.replace('{count}', count);
  }
  if (count === 1) {
    return resourceObject.one.replace('{count}', count);
  }

  // Check 11, 111, 211, 311, 1011 etc.
  if (locale === 'is' && (count - 11) % 100 === 0) {
    return resourceObject.many.replace('{count}', count);
  }

  // Check 21, 31, 121, 1031 etc.
  if (locale === 'is' && count % 10 === 1) {
    return resourceObject.one.replace('{count}', count);
  }

  // Check 112, 113, 114, 212, 413, 514 etc. in polish
  if (locale === 'pl' && resourceObject.few !== undefined) {
    if (
      (count - 12) % 100 === 0 ||
      (count - 13) % 100 === 0 ||
      (count - 14) % 100 === 0
    ) {
      return resourceObject.many.replace('{count}', count);
    }
    if ((count > 1 && count < 5) || (count % 10 > 1 && count % 10 < 5)) {
      return resourceObject.few.replace('{count}', count);
    }
  }

  // Check 100, 200, 300 etc. in polish
  if (locale === 'pl' && count % 100 === 0) {
    return resourceObject.one.replace('{count}', count);
  }

  return resourceObject.many.replace('{count}', count);
};

export function r(
  locale: LocaleType,
  resources: IResource | undefined,
  count?: number,
) {
  if (resources === undefined) {
    if (process.env.NODE_ENV === 'development') {
      throw new Error(`Undefined resource for locale ${locale}`);
    }
    return '';
  }

  if (count === undefined) {
    return getResources(locale, resources);
  }

  if (count !== undefined) {
    return getCountSpecificResource(locale, resources, count);
  }
}

export function res(locale: LocaleType) {
  return (resources: IResource, count?: number) => r(locale, resources, count);
}
